import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import api from 'state/axios';
import { format, parse } from "date-fns";
import toast from 'react-hot-toast';
import { Formik } from "formik";
import * as yup from "yup";
import { useTheme, useMediaQuery, Box, Typography, 
    TextField, Button, CircularProgress, FormControlLabel, 
    Checkbox, MenuItem, Select, FormControl, InputLabel, InputAdornment,  
} from '@mui/material';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const NonCrewMember = () => {
    const { id } = useParams();    
    const navigate = useNavigate();    
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [nonCrewMember, setNonCrewMember] = useState({});
    
    const initialValues = {        
        staffCode: nonCrewMember.staffCode || '',
        staffName: nonCrewMember.staffName || '',
        shortName: nonCrewMember.shortName || '',
        gender: nonCrewMember.gender || 'ML',
        birthday: nonCrewMember.birthday ? format(new Date(nonCrewMember.birthday), "dd/MM/yyyy") : "",
        birthPlace: nonCrewMember.birthPlace || '',
        birthCountry: nonCrewMember.birthCountry || '',
        residenceCode: nonCrewMember.residenceCode || '',
        marital: nonCrewMember.marital || '',        
        address: nonCrewMember.address || '',
        city: nonCrewMember.city || '',
        state: nonCrewMember.state || '',
        zip: nonCrewMember.zip || '',
        email: nonCrewMember.email || '',
        contactCell: nonCrewMember.contactCell || '',
        contactCell2: nonCrewMember.contactCell2 || '',
        contactTel: nonCrewMember.contactTel || '',
        contactTel2: nonCrewMember.contactTel2 || '',
        contactCoMob: nonCrewMember.contactCoMob || '',
        contactCoMob2: nonCrewMember.contactCoMob2 || '',
        nextKinName: nonCrewMember.nextKinName || '',
        nextKinRelsn: nonCrewMember.nextKinRelsn || '',
        nextKinContact: nonCrewMember.nextKinContact || '',
        passportType: nonCrewMember.passportType || 'P',
        passportName: nonCrewMember.passportName || '',
        firstName: nonCrewMember.firstName || '',
        surname: nonCrewMember.surname || '',
        middleName: nonCrewMember.middleName || '',
        passportNumber: nonCrewMember.passportNumber || '',
        passportIssueDate: nonCrewMember.passportIssueDate ? format(new Date(nonCrewMember.passportIssueDate), "dd/MM/yyyy") : "",
        passportExpiryDate: nonCrewMember.passportExpiryDate ? format(new Date(nonCrewMember.passportExpiryDate), "dd/MM/yyyy") : "",
        passportCountry: nonCrewMember.passportCountry || '',
        nationality: nonCrewMember.nationality || '',        
        company: nonCrewMember.company || '',        
        department: nonCrewMember.department || '',        
        isActive: nonCrewMember.isActive !== undefined ? nonCrewMember.isActive : true,
    };            

    useEffect(() => {
        const fetchNonCrewMember = async () => {
            try {
                setLoading(true);
                if (id) {
                    const url = `/noncrewmembers/get/${id}`;
    
                    const res = await api.get(url);
                    const result = res.data;
                    setNonCrewMember(result);        
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchNonCrewMember();    
    }, [id]); 

    const checkoutSchema = yup.object().shape({
        staffCode: yup.string().required("required"),
        staffName: yup.string().required("required"),
        // email: yup.string().email("invalid email").required("required"),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {        
        try {            
            const url = id 
                ? `/noncrewmembers/put/${id}` 
                : '/noncrewmembers/new';
            const method = id ? "put" : "post";
            
            await api({
                method,
                url,
                data: values,                
            });

            toast.success('Non crew member updated successfully.');
            onSubmitProps.resetForm();            
            navigate("/noncrewmembers");
        } catch (error) {
            setError(error);
            toast.error(error?.message || 'An error occurred during submission!');
        }
    };
    
    return (
        <Box className="main-container-box">              
            <FlexBetween>
                <Header 
                    title="Non Crew Member" 
                    subtitle={`${loading ? 'Loading...' : id ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/noncrewmembers")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >  
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                            <form onSubmit={handleSubmit}>                                
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
                                    }}              
                                >
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Staff Code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.staffCode}
                                        name="staffCode"
                                        error={!!touched.staffCode && !!errors.staffCode}
                                        helperText={touched.staffCode && errors.staffCode}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Staff Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.staffName}
                                        name="staffName"
                                        error={!!touched.staffName && !!errors.staffName}
                                        helperText={touched.staffName && errors.staffName}
                                        sx={{ gridColumn: "span 6" }}
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Short Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.shortName}
                                        name="shortName"
                                        error={!!touched.shortName && !!errors.shortName}
                                        helperText={touched.shortName && errors.shortName}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <FormControl 
                                        variant="filled"
                                        sx={{ gridColumn: "span 6" }}
                                    >
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                            name="gender"
                                            value={values.gender}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="ML">Male</MenuItem>
                                            <MenuItem value="FM">Female</MenuItem>
                                            <MenuItem value="UN">Undefined</MenuItem>
                                        </Select>
                                    </FormControl>                                
                                    <DatePicker         
                                        label="Birthday"
                                        value={values.birthday ? parse(values.birthday, "dd/MM/yyyy", new Date()) : null}
                                        onChange={(newValue) => {
                                            const formattedDate = newValue ? format(newValue, "dd/MM/yyyy") : "";
                                            setFieldValue("birthday", formattedDate);
                                        }}
                                        format="dd/MM/yyyy"
                                        sx={{ gridColumn: "span 6" }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="filled"                                           
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setFieldValue("birthday", "")}>
                                                                <ClearIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    <TextField
                                        variant="filled"
                                        label="Place of Birth"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.birthPlace}
                                        name="birthPlace"
                                        error={!!touched.birthPlace && !!errors.birthPlace}
                                        helperText={touched.birthPlace && errors.birthPlace}
                                        sx={{ gridColumn: "span 6" }}
                                    />        
                                    <TextField
                                        variant="filled"
                                        label="Country of Birth"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.birthCountry}
                                        name="birthCountry"
                                        error={!!touched.birthCountry && !!errors.birthCountry}
                                        helperText={touched.birthCountry && errors.birthCountry}
                                        sx={{ gridColumn: "span 6" }}
                                    /> 
                                    <TextField
                                        variant="filled"
                                        label="Residence Code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.residenceCode}
                                        name="residenceCode"
                                        error={!!touched.residenceCode && !!errors.residenceCode}
                                        helperText={touched.residenceCode && errors.residenceCode}
                                        sx={{ gridColumn: "span 6" }}
                                    />                           
                                    <TextField
                                        variant="filled"
                                        label="Marital Status"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.marital}
                                        name="marital"
                                        error={!!touched.marital && !!errors.marital}
                                        helperText={touched.marital && errors.marital}
                                        sx={{ gridColumn: "span 6" }}
                                    />                                    
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Email"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.email}
                                        name="email"
                                        error={!!touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                        sx={{ gridColumn: "span 6" }}
                                    />                                                                   
                                    {/* <FormControl variant="filled" sx={{ gridColumn: "span 6" }} >
                                        <InputLabel>Rank</InputLabel>
                                        <Select
                                            label="Rank"
                                            name="rank"
                                            value={values.rank}
                                            onChange={(e) => setFieldValue("rank", e.target.value)}
                                        >
                                            {crewRanks.map((rank) => (
                                                <MenuItem key={rank._id} value={rank.rankCode}>
                                                    {rank.rankCode} - {rank.description}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>*/}                                    
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Address"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.address}
                                        name="address"
                                        error={!!touched.address && !!errors.address}
                                        helperText={touched.address && errors.address}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="City"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.city}
                                        name="city"
                                        error={!!touched.city && !!errors.city}
                                        helperText={touched.city && errors.city}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="State"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.state}
                                        name="state"
                                        error={!!touched.state && !!errors.state}
                                        helperText={touched.state && errors.state}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Zip"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.zip}
                                        name="zip"
                                        error={!!touched.zip && !!errors.zip}
                                        helperText={touched.zip && errors.zip}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Cell"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactCell}
                                        name="contactCell"
                                        error={!!touched.contactCell && !!errors.contactCell}
                                        helperText={touched.contactCell && errors.contactCell}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Cell 2"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactCell2}
                                        name="contactCell2"
                                        error={!!touched.contactCell2 && !!errors.contactCell2}
                                        helperText={touched.contactCell2 && errors.contactCell2}
                                        sx={{ gridColumn: "span 6" }}                
                                    />                                    
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Tel"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactTel}
                                        name="contactTel"
                                        error={!!touched.contactTel && !!errors.contactTel}
                                        helperText={touched.contactTel && errors.contactTel}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Tel 2"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactTel2}
                                        name="contactTel2"
                                        error={!!touched.contactTel2 && !!errors.contactTel2}
                                        helperText={touched.contactTel2 && errors.contactTel2}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Co Mobile"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactCoMob}
                                        name="contactCoMob"
                                        error={!!touched.contactCoMob && !!errors.contactCoMob}
                                        helperText={touched.contactCoMob && errors.contactCoMob}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Contact Co Mobile 2"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.contactCoMob2}
                                        name="contactCoMob2"
                                        error={!!touched.contactCoMob2 && !!errors.contactCoMob2}
                                        helperText={touched.contactCoMob2 && errors.contactCoMob2}
                                        sx={{ gridColumn: "span 6" }}                
                                    />                                    
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Next Kin Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.nextKinName}
                                        name="nextKinName"
                                        error={!!touched.nextKinName && !!errors.nextKinName}
                                        helperText={touched.nextKinName && errors.nextKinName}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Next Kin Relationship"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.nextKinRelsn}
                                        name="nextKinRelsn"
                                        error={!!touched.nextKinRelsn && !!errors.nextKinRelsn}
                                        helperText={touched.nextKinRelsn && errors.nextKinRelsn}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Next Kin Contact"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.nextKinContact}
                                        name="nextKinContact"
                                        error={!!touched.nextKinContact && !!errors.nextKinContact}
                                        helperText={touched.nextKinContact && errors.nextKinContact}
                                        sx={{ gridColumn: "span 6" }}                
                                    />              
                                    <FormControl 
                                        variant="filled"
                                        sx={{ gridColumn: "span 6" }}
                                    >
                                        <InputLabel>Passport Type</InputLabel>
                                        <Select
                                            name="passportType"
                                            value={values.passportType}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                        >
                                            <MenuItem value="P">Personal</MenuItem>
                                            <MenuItem value="D">Diplomatic</MenuItem>
                                            <MenuItem value="F">Official</MenuItem>
                                        </Select>
                                    </FormControl>                      
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Passport Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.passportName}
                                        name="passportName"
                                        error={!!touched.passportName && !!errors.passportName}
                                        helperText={touched.passportName && errors.passportName}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="First Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.firstName}
                                        name="firstName"
                                        error={!!touched.firstName && !!errors.firstName}
                                        helperText={touched.firstName && errors.firstName}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Surname"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.surname}
                                        name="surname"
                                        error={!!touched.surname && !!errors.surname}
                                        helperText={touched.surname && errors.surname}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Middle Name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.middleName}
                                        name="middleName"
                                        error={!!touched.middleName && !!errors.middleName}
                                        helperText={touched.middleName && errors.middleName}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Passport Number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.passportNumber}
                                        name="passportNumber"
                                        error={!!touched.passportNumber && !!errors.passportNumber}
                                        helperText={touched.passportNumber && errors.passportNumber}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <DatePicker
                                        variant="filled"                                    
                                        label="Passport Issue Date"
                                        value={values.passportIssueDate ? parse(values.passportIssueDate, "dd/MM/yyyy", new Date()) : null}
                                        onChange={(newValue) => {
                                            const formattedDate = newValue ? format(newValue, "dd/MM/yyyy") : "";
                                            setFieldValue("passportIssueDate", formattedDate);
                                        }}
                                        format="dd/MM/yyyy"
                                        sx={{ gridColumn: "span 6" }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}                                            
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setFieldValue("passportIssueDate", "")}>
                                                                <ClearIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    <DatePicker
                                        variant="filled"                                    
                                        label="Passport Expiry Date"
                                        value={values.passportExpiryDate ? parse(values.passportExpiryDate, "dd/MM/yyyy", new Date()) : null}
                                        onChange={(newValue) => {
                                            const formattedDate = newValue ? format(newValue, "dd/MM/yyyy") : "";
                                            setFieldValue("passportExpiryDate", formattedDate);
                                        }}
                                        format="dd/MM/yyyy"
                                        sx={{ gridColumn: "span 6" }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}                                         
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setFieldValue("passportExpiryDate", "")}>
                                                                <ClearIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Passport Country"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.passportCountry}
                                        name="passportCountry"
                                        error={!!touched.passportCountry && !!errors.passportCountry}
                                        helperText={touched.passportCountry && errors.passportCountry}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Nationality"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.nationality}
                                        name="nationality"
                                        error={!!touched.nationality && !!errors.nationality}
                                        helperText={touched.nationality && errors.nationality}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Company"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.company}
                                        name="company"
                                        error={!!touched.company && !!errors.company}
                                        helperText={touched.company && errors.company}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <TextField
                                        variant="filled"
                                        type="text"
                                        label="Department"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.department}
                                        name="department"
                                        error={!!touched.department && !!errors.department}
                                        helperText={touched.department && errors.department}
                                        sx={{ gridColumn: "span 6" }}                
                                    />
                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 6' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {id ? "Update" : "Create"}
                                    </Button>                                    
                                </Box>                            
                            </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default NonCrewMember;
